import { cn } from "@/lib/utils";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React from "react";

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & {
    enableShadows?: boolean;
    /** Direction for scroll shadows: "x" for horizontal, "y" for vertical, "both" for both directions */
    shadowDirection?: "x" | "y" | "both";
    /** Prevents click events from propagating to parent elements after scrolling */
    blockClickOnScroll?: boolean;
    /** Additional className for the internal scrollbar (e.g., "mt-2" for spacing) */
    scrollBarClassName?: string;
    /** Additional className for the scrolling viewport (e.g. "max-h-80" to cap its height) */
    viewportClassName?: string;
    /** Enable drag-to-scroll on the content area (disables click events on content while dragging) */
    dragToScroll?: boolean;
  }
>(
  (
    {
      className,
      children,
      enableShadows = false,
      shadowDirection = "x",
      blockClickOnScroll = false,
      scrollBarClassName,
      viewportClassName,
      dragToScroll = false,
      ...props
    },
    ref,
  ) => {
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [canScrollTop, setCanScrollTop] = React.useState(false);
    const [canScrollBottom, setCanScrollBottom] = React.useState(false);
    const [isDragging, setIsDragging] = React.useState(false);
    const rootRef = React.useRef<React.ElementRef<
      typeof ScrollAreaPrimitive.Root
    > | null>(null);
    const viewportRef = React.useRef<HTMLElement | null>(null);

    // Track scrolling to block click propagation
    const didScrollRef = React.useRef(false);
    const startPosRef = React.useRef({ x: 0, y: 0 });
    const lastPosRef = React.useRef({ x: 0, y: 0 });

    const setRefs = React.useCallback(
      (node: React.ElementRef<typeof ScrollAreaPrimitive.Root> | null) => {
        rootRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<any>).current = node;
        }
      },
      [ref],
    );

    // Store viewport ref for drag-to-scroll
    React.useEffect(() => {
      viewportRef.current = rootRef.current?.querySelector(
        "[data-radix-scroll-area-viewport]",
      ) as HTMLElement | null;
    }, []);

    React.useEffect(() => {
      if (!enableShadows) return;

      const viewport = viewportRef.current;
      if (!viewport) return;

      const checkScroll = () => {
        const {
          scrollLeft,
          scrollWidth,
          clientWidth,
          scrollTop,
          scrollHeight,
          clientHeight,
        } = viewport;

        if (shadowDirection === "x" || shadowDirection === "both") {
          setCanScrollLeft(scrollLeft > 0);
          setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }

        if (shadowDirection === "y" || shadowDirection === "both") {
          setCanScrollTop(scrollTop > 0);
          setCanScrollBottom(scrollTop < scrollHeight - clientHeight - 1);
        }
      };

      checkScroll();

      viewport.addEventListener("scroll", checkScroll);
      const resizeObserver = new ResizeObserver(() => checkScroll());
      resizeObserver.observe(viewport);

      return () => {
        viewport.removeEventListener("scroll", checkScroll);
        resizeObserver.disconnect();
      };
    }, [enableShadows, shadowDirection, children]);

    const handlePointerDown = React.useCallback(
      (e: React.PointerEvent) => {
        if (!blockClickOnScroll) return;
        didScrollRef.current = false;
        startPosRef.current = { x: e.clientX, y: e.clientY };
      },
      [blockClickOnScroll],
    );

    const handlePointerMove = React.useCallback(
      (e: React.PointerEvent) => {
        if (!blockClickOnScroll) return;
        const dx = Math.abs(e.clientX - startPosRef.current.x);
        const dy = Math.abs(e.clientY - startPosRef.current.y);
        if (dx > 5 || dy > 5) {
          didScrollRef.current = true;
        }
      },
      [blockClickOnScroll],
    );

    const handleClickCapture = React.useCallback(
      (e: React.MouseEvent) => {
        if ((blockClickOnScroll || dragToScroll) && didScrollRef.current) {
          e.stopPropagation();
          e.preventDefault();
          didScrollRef.current = false;
        }
      },
      [blockClickOnScroll, dragToScroll],
    );

    // Drag-to-scroll handlers
    const handleDragPointerDown = React.useCallback(
      (e: React.PointerEvent) => {
        if (!dragToScroll) return;
        // Don't start drag if clicking on the scrollbar
        if (
          (e.target as HTMLElement).closest(
            "[data-radix-scroll-area-scrollbar]",
          )
        ) {
          return;
        }
        setIsDragging(true);
        lastPosRef.current = { x: e.clientX, y: e.clientY };
        startPosRef.current = { x: e.clientX, y: e.clientY };
        didScrollRef.current = false;
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        e.preventDefault();
      },
      [dragToScroll],
    );

    const handleDragPointerMove = React.useCallback(
      (e: React.PointerEvent) => {
        if (!dragToScroll || !isDragging) return;
        const viewport = viewportRef.current;
        if (!viewport) return;

        const dx = e.clientX - lastPosRef.current.x;
        const dy = e.clientY - lastPosRef.current.y;
        viewport.scrollLeft -= dx;
        viewport.scrollTop -= dy;
        lastPosRef.current = { x: e.clientX, y: e.clientY };

        // Mark as scrolled if moved more than 5px from start
        const totalDx = Math.abs(e.clientX - startPosRef.current.x);
        const totalDy = Math.abs(e.clientY - startPosRef.current.y);
        if (totalDx > 5 || totalDy > 5) {
          didScrollRef.current = true;
        }
      },
      [dragToScroll, isDragging],
    );

    const handleDragPointerUp = React.useCallback(
      (e: React.PointerEvent) => {
        if (!dragToScroll) return;
        setIsDragging(false);
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      },
      [dragToScroll],
    );

    return (
      <ScrollAreaPrimitive.Root
        ref={setRefs}
        className={cn(
          /*
            clip, not hidden: only the viewport scrolls. A hidden root can
            still be scrolled by the browser to bring a focused element (a
            checkbox, a button) into view, which shifts the whole viewport up
            and leaves a blank band below it; a clipped one can't.
            Unlike hidden, clip keeps a flex or grid item's automatic minimum
            size, so a wide row would push the page sideways: min-w-0 and
            min-h-0 let it shrink as hidden did.
          */
          "relative min-h-0 min-w-0 overflow-clip",
          dragToScroll && "select-none",
          dragToScroll && (isDragging ? "cursor-grabbing" : "cursor-grab"),
          className,
        )}
        onPointerDown={dragToScroll ? handleDragPointerDown : handlePointerDown}
        onPointerMove={dragToScroll ? handleDragPointerMove : handlePointerMove}
        onPointerUp={dragToScroll ? handleDragPointerUp : undefined}
        onClickCapture={handleClickCapture}
        {...props}
      >
        <ScrollAreaPrimitive.Viewport
          className={cn("h-full w-full rounded-[inherit]", viewportClassName)}
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        {enableShadows &&
          (shadowDirection === "x" || shadowDirection === "both") && (
            <>
              <div
                className={cn(
                  "absolute left-0 top-0 bottom-0 w-8 pointer-events-none z-9 transition-opacity duration-300",
                  canScrollLeft ? "opacity-100" : "opacity-0",
                )}
                style={{
                  background:
                    "linear-gradient(to right, rgba(0,0,0,0.5) 0%, transparent 100%)",
                }}
              />
              <div
                className={cn(
                  "absolute right-0 top-0 bottom-0 w-8 pointer-events-none z-9 transition-opacity duration-300",
                  canScrollRight ? "opacity-100" : "opacity-0",
                )}
                style={{
                  background:
                    "linear-gradient(to left, rgba(0,0,0,0.8) 0%, transparent 100%)",
                }}
              />
            </>
          )}
        {enableShadows &&
          (shadowDirection === "y" || shadowDirection === "both") && (
            <>
              <div
                className={cn(
                  "absolute top-0 left-0 right-2 h-8 pointer-events-none z-9 transition-opacity duration-300",
                  canScrollTop ? "opacity-100" : "opacity-0",
                )}
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)",
                }}
              />
              <div
                className={cn(
                  "absolute bottom-0 left-0 right-0 h-8 pointer-events-none z-9 transition-opacity duration-300",
                  canScrollBottom ? "opacity-100" : "opacity-0",
                )}
                style={{
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)",
                }}
              />
            </>
          )}
        <ScrollBar className={scrollBarClassName} />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.ScrollAreaScrollbar
  > & {
    scrollBarColor?: string;
  }
>(({ className, orientation = "vertical", scrollBarColor, ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb
      className={cn("relative flex-1 rounded-full bg-border", scrollBarColor)}
    />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
